import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TimedMessageForm from './TimedMessageForm';
import TimedMessagesSkeleton from './TimedMessagesSkeleton';
import {
  TimedMessage,
  CreateTimedMessageInput,
  getTimedMessages,
  createTimedMessage,
  updateTimedMessage,
  deleteTimedMessage,
  toggleTimedMessage,
  getDayOptions
} from '../lib/timedMessages';
import { loadRumbleSettings } from '../lib/rumbleApi';
import { isUserWhitelisted } from '../lib/whitelist';

interface TimedMessagesProps {
  onLogin?: () => void;
  onNavigateToSettings?: () => void;
}

const TimedMessages: React.FC<TimedMessagesProps> = ({ onLogin, onNavigateToSettings }) => {
  const [messages, setMessages] = useState<TimedMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState<TimedMessage | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState<string | React.ReactNode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; messageId: string | null }>({
    isOpen: false,
    messageId: null
  });
  const [hasRumbleApi, setHasRumbleApi] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  const { user } = useAuth();

  // Check if user has Rumble API connected
  useEffect(() => {
    const checkRumbleApi = async () => {
      if (user) {
        try {
          const settings = await loadRumbleSettings();
          setHasRumbleApi(settings?.api_key && settings?.rumble_username ? true : false);
        } catch (err) {
          setHasRumbleApi(false);
        }
      }
    };
    checkRumbleApi();
  }, [user]);

  // Check if user is whitelisted for timed messages
  useEffect(() => {
    const checkWhitelist = async () => {
      if (user) {
        try {
          const whitelisted = await isUserWhitelisted('timed-messages');
          setIsWhitelisted(whitelisted);
        } catch (err) {
          setIsWhitelisted(false);
        }
      }
    };
    checkWhitelist();
  }, [user]);

  // Load messages on component mount
  useEffect(() => {
    if (user) {
      loadMessages();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await getTimedMessages();
      setMessages(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMessage = async (input: CreateTimedMessageInput) => {
    try {
      setFormLoading(true);
      await createTimedMessage(input);
      await loadMessages();
      setShowForm(false);
      setError(null);
    } catch (err) {
      // Check for specific error types
      if (err instanceof Error && err.message === 'NO_RUMBLE_API') {
        setError(
          <>
            You need to connect your Rumble Live Stream API in the{' '}
            <button
              onClick={() => onNavigateToSettings?.()}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Settings
            </button>
            {' '}tab before creating timed messages.
          </>
        );
        setFormLoading(false);
        return; // Don't throw, we handled it
      } else if (err instanceof Error && err.message === 'NOT_WHITELISTED') {
        setError(
          <>
            You need to be whitelisted to create timed messages. Please reach out to{' '}
            <a
              href="https://x.com/whizkick"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              @whizkick on X
            </a>
            {' '}for access.
          </>
        );
        setFormLoading(false);
        return; // Don't throw, we handled it
      }
      throw err; // Let form handle other errors
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateMessage = async (input: CreateTimedMessageInput) => {
    if (!editingMessage) return;
    
    try {
      setFormLoading(true);
      await updateTimedMessage(editingMessage.id!, input);
      await loadMessages();
      setEditingMessage(null);
      setError(null);
    } catch (err) {
      throw err; // Let form handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteMessage = async (id: string) => {
    try {
      await deleteTimedMessage(id);
      await loadMessages();
      setDeleteConfirm({ isOpen: false, messageId: null });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete message');
    }
  };

  const confirmDelete = (id: string) => {
    setDeleteConfirm({ isOpen: true, messageId: id });
  };

  const handleToggleMessage = async (id: string, enabled: boolean) => {
    try {
      await toggleTimedMessage(id, enabled);
      await loadMessages();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle message');
    }
  };

  const formatSchedule = (message: TimedMessage) => {
    // Use new format if available, fallback to legacy format
    if (message.interval && message.spacing) {
      return `Every ${message.interval} minutes, ${message.spacing} messages apart`;
    }
    
    if (message.schedule_type === 'interval') {
      const minutes = message.interval_minutes || 5;
      if (minutes < 60) {
        return `Every ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        let result = `Every ${hours} hour${hours !== 1 ? 's' : ''}`;
        if (remainingMinutes > 0) {
          result += ` ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`;
        }
        return result;
      }
    } else if (message.schedule_type === 'specific_time') {
      const time = message.specific_time || '12:00';
      const timezone = message.specific_timezone || 'UTC';
      const days = message.specific_days || [1, 2, 3, 4, 5, 6, 7];
      const dayOptions = getDayOptions();
      
      const dayLabels = days
        .map((d: number) => dayOptions.find((opt: any) => opt.value === d)?.label)
        .filter(Boolean);
      
      const dayText = days.length === 7 
        ? 'Daily' 
        : days.length === 5 && !days.includes(6) && !days.includes(7)
        ? 'Weekdays'
        : dayLabels.join(', ');
      
      return `${dayText} at ${time} (${timezone})`;
    }
    
    // Fallback for unknown format
    return 'Custom schedule';
  };

  const formatNextRun = (nextScheduledAt: string | null) => {
    if (!nextScheduledAt) return 'Not scheduled';
    
    const date = new Date(nextScheduledAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    
    if (diffMs < 0) return 'Overdue';
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `In ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else if (diffMinutes > 0) {
      return `In ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`;
    } else {
      return 'Very soon';
    }
  };

  // Show skeleton while loading
  if (loading) {
    return <TimedMessagesSkeleton />;
  }

  if (!user) {
    return (
      <section id="timed-messages" className="content-section flex items-center justify-center min-h-[500px]">
        <div className="max-w-md w-full text-center p-8 border-2 border-white bg-black">
          <div className="mb-6">
            <i className="fa-solid fa-lock text-6xl text-white mb-4 inline-block"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">Authentication Required</h2>
          <p className="text-gray-300 mb-6 text-base leading-relaxed">
            Sign in to create and manage your timed messages. We use magic link authentication - no passwords needed!
          </p>

          <div className="space-y-4">
            <button
              onClick={onLogin}
              className="paint-button w-full text-lg py-3"
            >
              <i className="fa-solid fa-envelope mr-2"></i>
              Sign In with Magic Link
            </button>

            <div className="flex items-center gap-2 text-xs text-gray-400 justify-center">
              <i className="fa-solid fa-info-circle"></i>
              <span>We'll send a secure link to your email</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="timed-messages" className="content-section">
      {/* Closed Beta Alert */}
      {!hasRumbleApi ? (
        <div className="mb-6 p-4 border-2 border-yellow-500 bg-yellow-900 bg-opacity-20 text-yellow-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-yellow-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Setup Required</p>
              <p className="text-sm mb-2">
                Before creating timed messages, you need to connect your Rumble Live Stream API in{' '}
                <button
                  onClick={() => onNavigateToSettings?.()}
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  Settings
                </button>
                .
              </p>
              <p className="text-sm">
                You'll also need to be whitelisted for closed beta access. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      ) : !isWhitelisted ? (
        <div className="mb-6 p-4 border-2 border-blue-500 bg-blue-900 bg-opacity-20 text-blue-200">
          <div className="flex items-start gap-3">
            <i className="fa-solid fa-info-circle text-blue-400 mt-1"></i>
            <div>
              <p className="font-semibold mb-1">Closed Beta Access</p>
              <p className="text-sm">
                Timed messages require whitelist access. Please reach out to{' '}
                <a
                  href="https://x.com/whizkick"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 underline font-medium"
                >
                  @whizkick on X
                </a>
                {' '}for whitelisting.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex justify-between items-center mb-6 header-border pb-4">
        <h2 className="text-xl font-semibold text-white">timed messages</h2>
        <button
          className="paint-button paint-button-sm"
          onClick={() => {
            if (!hasRumbleApi) {
              setError(
                <>
                  You need to connect your Rumble Live Stream API in the{' '}
                  <button
                    onClick={() => onNavigateToSettings?.()}
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Settings
                  </button>
                  {' '}tab before creating timed messages.
                </>
              );
            } else if (!isWhitelisted) {
              setError(
                <>
                  You need to be whitelisted to create timed messages. Please reach out to{' '}
                  <a
                    href="https://x.com/whizkick"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    @whizkick on X
                  </a>
                  {' '}for access.
                </>
              );
            } else if (messages.length >= 5) {
              setError('You have reached the maximum limit of 5 timed messages. Please delete an existing message to create a new one.');
            } else {
              setShowForm(true);
            }
          }}
        >
          <i className="fa-solid fa-plus mr-1"></i>
          add message
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
          <i className="fa-solid fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {messages.length === 0 ? (
        <div className="text-center p-8">
          <i className="fa-solid fa-clock text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-semibold text-white mb-2">No Timed Messages</h3>
          <p className="text-gray-400 mb-4">Create your first timed message to get started</p>
          <button
            className="paint-button"
            onClick={() => {
              if (!hasRumbleApi) {
                setError(
                  <>
                    You need to connect your Rumble Live Stream API in the{' '}
                    <button
                      onClick={() => onNavigateToSettings?.()}
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      Settings
                    </button>
                    {' '}tab before creating timed messages.
                  </>
                );
              } else if (!isWhitelisted) {
                setError(
                  <>
                    You need to be whitelisted to create timed messages. Please reach out to{' '}
                    <a
                      href="https://x.com/whizkick"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      @whizkick on X
                    </a>
                    {' '}for access.
                  </>
                );
              } else {
                setShowForm(true);
              }
            }}
          >
            <i className="fa-solid fa-plus mr-2"></i>
            Add Your First Message
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`paint-card p-4 ${!message.enabled ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <button
                      onClick={() => handleToggleMessage(message.id!, !message.enabled)}
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        message.enabled 
                          ? 'bg-green-500 border-green-500 text-white' 
                          : 'border-gray-400 hover:border-white'
                      }`}
                      title={message.enabled ? 'Disable message' : 'Enable message'}
                    >
                      {message.enabled && <i className="fa-solid fa-check text-xs"></i>}
                    </button>
                    <span className={`font-medium ${message.enabled ? 'text-white' : 'text-gray-400'}`}>
                      {formatSchedule(message)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatNextRun(message.next_scheduled_at || null)}
                    </span>
                  </div>
                  
                  <p className={`text-sm mb-2 ${message.enabled ? 'text-gray-300' : 'text-gray-500'}`}>
                    "{message.message || message.text || ''}"
                  </p>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span>Sent {message.total_sent_count || 0} times</span>
                    {message.last_sent_at && (
                      <span>Last sent: {new Date(message.last_sent_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => setEditingMessage(message)}
                    className="text-gray-400 hover:text-white transition-colors"
                    title="Edit message"
                  >
                    <i className="fa-solid fa-edit"></i>
                  </button>
                  <button
                    onClick={() => confirmDelete(message.id!)}
                    className="text-gray-400 hover:text-red-400 transition-colors"
                    title="Delete message"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Message Form */}
      {showForm && (
        <TimedMessageForm
          onSubmit={handleCreateMessage}
          onCancel={() => setShowForm(false)}
          loading={formLoading}
        />
      )}

      {/* Edit Message Form */}
      {editingMessage && (
        <TimedMessageForm
          onSubmit={handleUpdateMessage}
          onCancel={() => setEditingMessage(null)}
          loading={formLoading}
          initialData={{
            message: editingMessage.text || editingMessage.message,
            schedule_type: editingMessage.schedule_type,
            interval_minutes: editingMessage.interval_minutes,
            specific_time: editingMessage.specific_time,
            specific_timezone: editingMessage.specific_timezone,
            specific_days: editingMessage.specific_days,
            enabled: editingMessage.enabled
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-black border-2 border-white p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Delete Message</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete this timed message? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, messageId: null })}
                className="paint-button paint-button-sm bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteConfirm.messageId && handleDeleteMessage(deleteConfirm.messageId)}
                className="paint-button paint-button-sm bg-red-600 hover:bg-red-700"
              >
                <i className="fa-solid fa-trash mr-2"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TimedMessages;